import { collectAllPages, exists } from "@examspark/common";
import {
  IDSK,
  ModelListInput,
  ModelResourceConnection,
  ModelWorkspaceConnection,
  ModelWorkspaceParentConnection,
  QueryListWorkspaceUsersArgs,
  QuerySearchArgs,
  SearchableResourceConnectionNew,
  UpdateWorkspaceInput,
  Workspace,
  WorkspaceShare,
  WorkspaceUser,
} from "@examspark/types";

import { GetWorkspaceUserResult, GraphQLAPI, queries } from "../..";
import { GraphQLQuery } from "../../..";
import { getResource, Params } from "../resource";

export * from "./calendar";
export * from "./products";

interface WorkspaceConfigTags {
  enabled?: boolean;
  tags?: {
    key: string;
    type: string;
    values: string[];
  }[];
}

export const getWorkspaceConfigTags = async (workspaceId: string) => {
  const result = await getResource({
    label: "GetWorkspaceConfigTags",
    fields: `
      id
      sk
      data
    `,
    variables: {
      id: workspaceId,
      sk: "workspace-config-tags",
    },
  });

  if (result.data?.getResource?.data) {
    return JSON.parse(
      result.data.getResource.data as string,
    ) as WorkspaceConfigTags;
  }
};

export const createWorkspaceUser = async (params: {
  workspaceId: string;
  userId: string;
  registrationCode?: string;
  acl?: {
    [key: string]: any;
  };
}) => {
  const result = await GraphQLAPI.client.query<{
    createWorkspaceUser: Workspace;
  }>({
    query: /* GraphQL */ `
      mutation CreateWorkspaceUser(
        $input: CreateWorkspaceInput!
        $registrationCode: String
      ) {
        createWorkspaceUser(
          input: $input
          registrationCode: $registrationCode
        ) {
          id
          sk
        }
      }
    `,
    variables: {
      input: {
        id: params.workspaceId,
        sk: ["workspace-user", params.userId].join("#"),
        workspaceId: params.workspaceId,
        workspaceUserId: params.userId,
        acl: JSON.stringify(params.acl),
      },
      registrationCode: params.registrationCode,
    },
  });

  return result.data?.createWorkspaceUser;
};

export const leaveWorkspace = async (params: {
  workspaceId: string;
  userId: string;
}) => {
  const result = await GraphQLAPI.client.query<{
    leaveWorkspaceV2: Workspace;
  }>({
    query: /* GraphQL */ `
      mutation LeaveWorkspace($input: DeleteWorkspaceInput!) {
        leaveWorkspaceV2(input: $input) {
          id
          sk
        }
      }
    `,
    variables: {
      input: {
        id: params.workspaceId,
        sk: ["workspace-user", params.userId].join("#"),
      },
    },
  });

  return result.data?.leaveWorkspaceV2;
};

export const getWorkspace = async (
  params?: Params<{
    input: IDSK;
  }>,
) => {
  const { label, fields, variables } = params || {};
  return GraphQLAPI.client.query<{
    getWorkspace: Workspace;
  }>({
    query: queries.workspace.getWorkspace({
      fields,
      label,
    }),
    variables,
  });
};

export const searchWorkspaces = async (params: Params<QuerySearchArgs>) => {
  let { variables } = params;
  if (variables == null) {
    variables = {};
  }

  variables.filter = {
    and: [{ sk: { eq: "workspace" } }, variables?.filter].filter(exists),
  };

  return GraphQLAPI.client.query<{
    adminSearch: SearchableResourceConnectionNew;
  }>({
    query: queries.search.adminSearch(params),
    variables,
  });
};

export const getWorkspaceUser = async (workspaceId: string, userId: string) => {
  const result = await getWorkspace({
    label: "GetWorkspaceUser",
    fields: `
      id
      sk
      schemaVersion
      name
      description
      createdAt
      updatedAt
      lastActiveAt
      acl
      isDisabled
      roles {
        id
        sk
        name
        description
        acl
      }
      tags {
        key
        value
      }
      workspace {
        id
        sk
        name
        description
        acl
      }
    `,
    variables: {
      input: {
        id: workspaceId,
        sk: `workspace-user#${userId.trim()}`,
      },
    },
  });

  return result.data?.getWorkspace as GetWorkspaceUserResult;
};

export const listWorkspaceParentsByParentWorkspaceId = async (
  workspaceId: string,
) => {
  const pages = await collectAllPages(async (nextToken) => {
    const result = await GraphQLAPI.client.query<
      GraphQLQuery<{
        listWorkspaceParentsByParentWorkspaceId: ModelWorkspaceParentConnection;
      }>
    >({
      query: /* GraphQL */ `
        query ListWorkspaceParentsByParentWorkspaceId($input: ModelListInput) {
          listWorkspaceParentsByParentWorkspaceId(input: $input) {
            items {
              id
              sk
              workspaceId
            }
            nextToken
          }
        }
      `,
      variables: {
        input: {
          id: workspaceId,
          nextToken,
        } satisfies ModelListInput,
      },
    });

    return result.data?.listWorkspaceParentsByParentWorkspaceId;
  });

  return pages.flatMap((page) => page.items ?? []);
};

export const listWorkspaces = async () => {
  const result = await GraphQLAPI.client.query<{
    listResourcesBySortKey: ModelResourceConnection;
  }>({
    query: /* GraphQL */ `
      query ListResourcesBySortKey(
        $sk: String
        $id: ModelIDKeyConditionInput
        $sortDirection: ModelSortDirection
        $filter: ModelResourceFilterInput
        $limit: Int
        $nextToken: String
      ) {
        listResourcesBySortKey(
          sk: $sk
          id: $id
          sortDirection: $sortDirection
          filter: $filter
          limit: $limit
          nextToken: $nextToken
        ) {
          items {
            id
            sk
            name
          }
          nextToken
        }
      }
    `,
    variables: {
      sk: "workspace",
    },
  });

  return result.data?.listResourcesBySortKey;
};

export const listWorkspaceUsersByUserId = async (userId: string) => {
  const result = await GraphQLAPI.client.query<
    GraphQLQuery<{ listWorkspaceUsers: ModelWorkspaceConnection }>
  >({
    query: /* GraphQL */ `
      query ListWorkspaceUsersById(
        $input: ModelListInput! # $nextToken: String
      ) {
        listWorkspaceUsers(input: $input) {
          items {
            id
            sk
          }
        }
      }
    `,
    variables: {
      input: {
        sk: `workspace-user#${userId}`,
      },
    } as QueryListWorkspaceUsersArgs,
  });

  return result.data?.listWorkspaceUsers.items as WorkspaceUser[] | undefined;
};

export const listInboundWorkspaceShares = async (
  workspaceId: string,
  appId?: string,
) => {
  const result = await GraphQLAPI.client.query<{
    listInboundWorkspaceShares: WorkspaceShare[];
  }>({
    query: /* GraphQL */ `
      query ListInboundWorkspaceShares(
        $authorizationContext: AuthorizationContextInput!
      ) {
        listInboundWorkspaceShares(
          authorizationContext: $authorizationContext
        ) {
          id
          sk
          expiresAt
          bankIds
        }
      }
    `,
    variables: {
      authorizationContext: {
        appId,
        workspaceId,
      },
    },
  });

  return result.data?.listInboundWorkspaceShares;
};

export const listOutboundWorkspaceShares = async (
  workspaceId: string,
  appId?: string,
) => {
  const result = await GraphQLAPI.client.query<{
    listOutboundWorkspaceShares: WorkspaceShare[];
  }>({
    query: /* GraphQL */ `
      query ListOutboundWorkspaceShares(
        $authorizationContext: AuthorizationContextInput!
      ) {
        listOutboundWorkspaceShares(
          authorizationContext: $authorizationContext
        ) {
          id
          sk
          expiresAt
          bankIds
        }
      }
    `,
    variables: {
      authorizationContext: {
        appId,
        workspaceId,
      },
    },
  });

  return result.data?.listOutboundWorkspaceShares;
};

export const updateWorkspace = async (
  workspaceId: string,
  data: Partial<UpdateWorkspaceInput>,
) => {
  return await GraphQLAPI.client.query({
    query: /* GraphQL */ `
      mutation UpdateWorkspace($input: UpdateWorkspaceInput!) {
        updateWorkspace(input: $input) {
          id
          sk
        }
      }
    `,
    variables: {
      input: {
        id: `${workspaceId}`,
        sk: "workspace",
        ...data,
      },
    },
  });
};

export const getWorkspaceParent = async (workspaceId: string) => {
  return await GraphQLAPI.client.query<{
    getWorkspaceParent: {
      id: string;
      sk: string;
      parentWorkspaceId: string;
    };
  }>({
    query: /* GraphQL */ `
      query GetWorkspaceParent($input: ModelGetInput!) {
        getWorkspaceParent(input: $input) {
          id
          sk
          parentWorkspaceId
        }
      }
    `,
    variables: {
      input: {
        id: `${workspaceId}`,
        sk: "workspace-parent",
      },
    },
  });
};

export const createWorkspaceParent = async (
  workspaceId: string,
  parentWorkspaceId: string,
) => {
  return await GraphQLAPI.client.query({
    query: /* GraphQL */ `
      mutation CreateWorkspaceParent($input: CreateWorkspaceParentInput!) {
        createWorkspaceParent(input: $input) {
          id
          sk
        }
      }
    `,
    variables: {
      input: {
        id: `${workspaceId}`,
        sk: "workspace-parent",
        parentWorkspaceId: parentWorkspaceId,
        workspaceId: parentWorkspaceId,
        schemaVersion: "2020-01-01",
      },
    },
  });
};

export const deleteWorkspaceParent = async (workspaceId: string) => {
  return await GraphQLAPI.client.query({
    query: /* GraphQL */ `
      mutation DeleteWorkspaceParent($input: DeleteWorkspaceParentInput!) {
        deleteWorkspaceParent(input: $input) {
          id
          sk
        }
      }
    `,
    variables: {
      input: {
        id: `${workspaceId}`,
        sk: "workspace-parent",
      },
    },
  });
};

export const deleteWorkspaceUser = async (
  workspaceId: string,
  userId: string,
) => {
  return GraphQLAPI.client.query<Workspace>({
    query: /* GraphQL */ `
      mutation DeleteWorkspaceUser($input: DeleteWorkspaceInput!) {
        deleteWorkspaceUser(input: $input) {
          id
          sk
        }
      }
    `,
    variables: {
      input: {
        id: workspaceId,
        sk: `workspace-user#${userId}`,
      },
    },
  });
};
